/* Atlas v3 — "Settings": the routed preferences surface.
   In the mockup this is an OVERLAY (AtlasSettings, internal state-nav);
   in this app /settings is a real ROUTED page, so it's a full-height
   surface wrapped in .v3-root with the shared shell (TickerStrip +
   BottomNav), NOT an overlay. No starfield (Overview-only).

   Handoff override: "borderless — amber section eyebrows + hairline rows
   + whitespace, no grouped card fills." So the mockup's filled SetCard
   grouping is DROPPED; sections are amber eyebrow labels + hairline-
   divided rows.

   Data wiring:
     • Appearance Day/Night/Auto ← theme.js setThemeMode/getStoredMode/
       resolveTheme. The stored choice (ink|night|auto) persists to
       localStorage('atlas_theme'); Night sets dataset.theme='night' across
       ALL v3 screens; Auto resolves Day/Night from the local clock. Applied
       on load by initTheme() (called in main.jsx), which also starts the
       Auto watcher so it flips at the 7pm/7am boundary without a reload.
     • Alerts + Defaults prefs (signal/price/briefing toggles, delivery
       channel, default tab/sort) ← useUserPref, same as the ADR-0039
       group: Clerk→Supabase signed-in, localStorage signed-out.
     • Account row + Sign out ← Clerk useUser/useClerk. */
import { useState } from 'react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { TickerCtx } from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { AgentAccess } from '@/v2/components/AgentAccess'
import { getStoredMode, setThemeMode, resolveTheme } from './theme'
import pkg from '../../../package.json'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

/* ── borderless section eyebrow ── */
function Eyebrow({ children }) {
  return <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent-soft))', margin: '28px 4px 6px' }}>{children}</div>
}

/* ── hairline row ── */
function Row({ label, sub, right, onClick, first }) {
  const [hot, setHot] = useState(false)
  const press = onClick ? {
    onPointerDown: () => setHot(true), onPointerUp: () => setHot(false),
    onPointerCancel: () => setHot(false), onPointerLeave: () => setHot(false),
  } : {}
  return (
    <div onClick={onClick} {...press}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px',
        cursor: onClick ? 'pointer' : 'default',
        background: hot ? 'hsl(var(--studio-surface) / 0.5)' : 'transparent',
        borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.5)',
        transition: 'background 140ms var(--studio-ease)',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...Sans, fontSize: 14, color: 'hsl(var(--studio-text))' }}>{label}</div>
        {sub && <div style={{ ...Sans, fontSize: 11.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, lineHeight: 1.4, textWrap: 'pretty' }}>{sub}</div>}
      </div>
      {right}
    </div>
  )
}

/* ── amber-line toggle (controlled) ── */
function Toggle({ on, onChange, label }) {
  return (
    <button role="switch" aria-checked={on} aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(!on) }}
      style={{
        width: 42, height: 24, borderRadius: 999, flexShrink: 0, cursor: 'pointer', padding: 0,
        position: 'relative', background: 'transparent',
        border: `1px solid ${on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-border))'}`,
        transition: 'border-color 180ms var(--studio-ease)',
      }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 21 : 3, width: 16, height: 16, borderRadius: '50%',
        background: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))',
        transition: 'left 180ms var(--studio-ease), background 180ms',
      }} />
    </button>
  )
}

/* ── inline segmented control (amber underline for active) ── */
function Seg({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
      {options.map((o) => {
        const on = o.v === value
        return (
          <button key={o.v} onClick={(e) => { e.stopPropagation(); onChange(o.v) }}
            style={{
              background: 'transparent', border: 'none', padding: '2px 1px', cursor: 'pointer',
              ...Mono, fontSize: 11, letterSpacing: '0.04em',
              color: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))',
              borderBottom: on ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent',
            }}>{o.l}</button>
        )
      })}
    </div>
  )
}

/* ── Appearance: Day / Night theme picker ── */
const THEMES = [
  { id: 'ink', label: 'Day', sub: 'TRUE BLACK · AMBER', bg: '#000000', surf: '#14110D', acc: 'hsl(41 56% 51%)', txt: 'hsl(40 41% 91%)' },
  { id: 'night', label: 'Night', sub: 'COOL VIOLET · AFTER HRS', bg: 'hsl(247 25% 7%)', surf: 'hsl(247 22% 14%)', acc: 'hsl(247 65% 66%)', txt: 'hsl(247 30% 92%)' },
]
function ThemePicker({ current, onPick }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {THEMES.map((th) => {
        const active = th.id === current
        return (
          <button key={th.id} onClick={() => onPick(th.id)}
            style={{
              textAlign: 'left', cursor: 'pointer', padding: 10, borderRadius: 'var(--studio-radius-lg)',
              background: 'hsl(var(--studio-surface) / 0.5)',
              border: active ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid hsl(var(--studio-border))',
              boxShadow: active ? '0 0 0 3px hsl(var(--studio-accent) / 0.18)' : 'none',
              transition: 'border-color 160ms, box-shadow 160ms',
            }}>
            <div style={{ height: 80, borderRadius: 'var(--studio-radius)', background: th.bg, padding: 9, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden', border: '1px solid hsl(0 0% 100% / 0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: th.acc, flexShrink: 0 }} />
                <span style={{ width: 28, height: 5, borderRadius: 3, background: th.txt, opacity: 0.85 }} />
              </div>
              <div style={{ height: 18, borderRadius: 4, background: th.surf }} />
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ width: 16, height: 6, borderRadius: 3, background: th.acc }} />
                <span style={{ width: 32, height: 6, borderRadius: 3, background: th.txt, opacity: 0.32 }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 9 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...Sans, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{th.label}</div>
                <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2, whiteSpace: 'nowrap' }}>{th.sub}</div>
              </div>
              {active && <span style={{ width: 18, height: 18, borderRadius: '50%', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>✓</span>}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function SettingsBody() {
  const { openTicker } = useStockDetail()
  const { user } = useUser()
  const { signOut } = useClerk()

  // Theme — real, persisted + applied across all v3 screens. `mode` is the stored
  // CHOICE (ink | night | auto). Auto resolves Day/Night from the local clock.
  const [mode, setMode] = useState(() => getStoredMode())
  const auto = mode === 'auto'
  const activeSwatch = auto ? resolveTheme('auto') : mode // which palette is showing now
  const pickTheme = (id) => { setMode(id); setThemeMode(id) } // swatch tap → manual (Auto off)
  const toggleAuto = (on) => { const m = on ? 'auto' : resolveTheme('auto'); setMode(m); setThemeMode(m) }

  // Real ADR-0039 prefs (Clerk→Supabase / localStorage), default ON.
  const [haptics, setHaptics] = useUserPref('haptics_enabled', true)
  const [livePulse, setLivePulse] = useUserPref('live_pulse_enabled', true)
  const [fridayReveal, setFridayReveal] = useUserPref('friday_reveal_enabled', true)

  // Alerts + Defaults — same backing as the group above (user_prefs).
  const [aSignals, setASignals] = useUserPref('alert_signals_enabled', true)
  const [aPrice, setAPrice] = useUserPref('alert_price_enabled', true)
  const [aBrief, setABrief] = useUserPref('alert_briefing_enabled', true)
  const [delivery, setDelivery] = useUserPref('alert_delivery', 'push')
  const [defTab, setDefTab] = useUserPref('default_tab', 'home')
  const [defSort, setDefSort] = useUserPref('default_sort', 'move')

  const name = user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress || 'Signed in'
  const email = user?.primaryEmailAddress?.emailAddress
  const initial = (name || '?').trim().charAt(0).toUpperCase()

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        <div style={{ flexShrink: 0, padding: '20px 20px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', marginBottom: 5 }}>PREFERENCES</div>
            <div style={{ ...Display, fontSize: 32, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>Settings</div>
          </div>
          <SearchPill />
        </div>

        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '8px 20px 28px' }}>
          {/* Appearance */}
          <Eyebrow>Appearance</Eyebrow>
          <ThemePicker current={activeSwatch} onPick={pickTheme} />
          <div style={{ marginTop: 6 }}>
            <Row first label="Auto by time of day" sub="Switch to Night from 7pm to 7am, your local time" right={<Toggle on={auto} onChange={toggleAuto} label="Toggle automatic night mode" />} />
          </div>
          <p style={{ ...Sans, fontSize: 12, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: '12px 4px 0', textWrap: 'pretty' }}>
            Night recolors the app&rsquo;s surfaces and accent to a cool after-hours violet. Your charts, company logos, and up/down colors stay exactly as they are. With Auto on, Atlas follows your phone&rsquo;s clock; tap a theme above to set it manually.
          </p>

          {/* Display (real ADR-0039 prefs) */}
          <Eyebrow>Display</Eyebrow>
          <Row first label="Haptics" sub="Light tap feedback on key moments (mobile only). Never on price events." right={<Toggle on={!!haptics} onChange={setHaptics} label="Toggle haptics" />} />
          <Row label="Live pulse on cards" sub="2px accent stripe on Tier 1 cards every 60s when the cron updates." right={<Toggle on={!!livePulse} onChange={setLivePulse} label="Toggle live pulse" />} />
          <Row label="Friday Insights reveal" sub="One-shot animation when the weekly brief drops on Fridays." right={<Toggle on={!!fridayReveal} onChange={setFridayReveal} label="Toggle Friday Insights reveal" />} />

          {/* Alerts (user_prefs) */}
          <Eyebrow>Alerts</Eyebrow>
          <Row first label="Signal alerts" sub="When Atlas spots a new high-conviction signal" right={<Toggle on={!!aSignals} onChange={setASignals} label="Toggle signal alerts" />} />
          <Row label="Price alerts" sub="Big moves on names you follow" right={<Toggle on={!!aPrice} onChange={setAPrice} label="Toggle price alerts" />} />
          <Row label="Morning briefing" sub="&ldquo;While You Slept&rdquo; overnight recap at 7:00 AM" right={<Toggle on={!!aBrief} onChange={setABrief} label="Toggle morning briefing" />} />
          <Row label="Deliver via" right={<Seg options={[{ v: 'push', l: 'PUSH' }, { v: 'email', l: 'EMAIL' }]} value={delivery} onChange={setDelivery} />} />

          {/* Defaults (user_prefs) */}
          <Eyebrow>Defaults</Eyebrow>
          <Row first label="Open to" sub="The tab Atlas lands on at launch" right={<Seg options={[{ v: 'home', l: 'OVERVIEW' }, { v: 'watch', l: 'MY STOCKS' }]} value={defTab} onChange={setDefTab} />} />
          <Row label="Default sort" right={<Seg options={[{ v: 'move', l: 'MOVE' }, { v: 'pct', l: '%' }, { v: 'az', l: 'A–Z' }]} value={defSort} onChange={setDefSort} />} />

          {/* Account (Clerk) */}
          <Eyebrow>Account</Eyebrow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px' }}>
            <span style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', display: 'flex', alignItems: 'center', justifyContent: 'center', ...Display, fontSize: 18, color: 'hsl(var(--studio-accent))' }}>{initial}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...Sans, fontSize: 14, color: 'hsl(var(--studio-text))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
              {email && email !== name && <div style={{ ...Mono, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>}
            </div>
          </div>
          <Row label={<span style={{ color: 'hsl(var(--studio-danger))' }}>Sign out</span>} onClick={() => signOut(() => { window.location.href = '/' })} />

          {/* Agent Access — mint API keys + the connect-your-agent manual */}
          <Eyebrow>Agent Access</Eyebrow>
          <AgentAccess />

          {/* About */}
          <Eyebrow>About</Eyebrow>
          <Row first label="Version" right={<span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text-subtle))' }}>{pkg.version}</span>} />
          <Row label="Repository" onClick={() => window.open('https://github.com/benjaminwolf36/atlas', '_blank', 'noopener,noreferrer')} right={<span style={{ ...Mono, fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>↗</span>} />
          <p style={{ ...Sans, fontSize: 11, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: '18px 4px 0', textWrap: 'pretty' }}>
            Atlas is research, not investment advice. Signals and convictions are model output &mdash; always do your own diligence.
          </p>
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function Settings() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <SettingsBody />
      </StockDetailProvider>
    </div>
  )
}
